import axios from 'axios';

// Desarrollo: baseURL vacío + `proxy` en package.json → backend local (sin CORS).
// Producción: REACT_APP_API_URL debe ser HTTPS público (ej. https://api.tu-dominio.gob.pe).
// Si queda vacío, baseURL es '' → mismas peticiones al mismo host (útil con proxy /api en Apache).
// Nunca uses http:// ni IPs internas en HTTPS: el navegador bloquea "Mixed Content".
const isProduction = process.env.NODE_ENV === 'production';
const envApi = (process.env.REACT_APP_API_URL || '').trim();
const productionBase = isProduction ? envApi : '';

const api = axios.create({
  baseURL: isProduction ? productionBase : '',
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Texto seguro para UI: FastAPI/Pydantic suele devolver `detail` como string o array de { msg, ... }. */
export function formatApiErrorDetail(error, fallback = 'Error en la solicitud') {
  const d = error?.response?.data?.detail;
  if (d == null || d === '') return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    const parts = d
      .map((item) => {
        if (item == null) return '';
        if (typeof item === 'string') return item;
        if (typeof item === 'object' && item.msg != null) return String(item.msg);
        return '';
      })
      .filter(Boolean);
    return parts.length ? parts.join(' ') : fallback;
  }
  if (typeof d === 'object' && d.msg != null) return String(d.msg);
  return fallback;
}

/**
 * URL base del API para enlaces <a href> e iframes a /uploads/...
 * Con npm start, axios usa baseURL vacío (proxy); /uploads en localhost:3000 la captura React Router.
 */
export function getPublicUploadsOrigin() {
  const base = api.defaults.baseURL;
  if (base != null && String(base).trim() !== '') {
    return String(base).replace(/\/$/, '');
  }
  const fromEnv =
    process.env.REACT_APP_DEV_UPLOADS_ORIGIN || process.env.REACT_APP_API_URL;
  const sameOrigin =
    typeof window !== 'undefined' ? window.location.origin : '';
  return (fromEnv || sameOrigin || '').replace(/\/$/, '');
}

// Interceptor para agregar el token en todas las peticiones
api.interceptors.request.use(
  (config) => {
    // Con FormData hay que dejar que el navegador ponga multipart + boundary; si no, el servidor responde 422.
    if (config.data instanceof FormData) {
      const h = config.headers;
      if (h && typeof h.delete === 'function') {
        h.delete('Content-Type');
      } else if (h) {
        delete h['Content-Type'];
        delete h['content-type'];
      }
    }
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de autenticación y conexión
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Manejar errores de red (backend no accesible)
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      console.error('Error de conexión con el backend:', {
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        message: 'No se pudo conectar al servidor. Verifique que el backend esté corriendo y accesible.'
      });
    }
    
    // Manejar errores de autenticación
    if (error.response?.status === 401) {
      // Si recibe un 401, redirigir al login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

export default api;

